import { SearchParam } from '@/models/search-param.models';
import { tableSortingStateToSearchParam } from '@/utils/search-param.function';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { Dispatch, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounce } from 'use-debounce';

export function useSyncFiltersToSearchParams<T>({
  filters,
  pagination,
  sorting,
  setPagination,
  searchParamsLoaded,
  defaultPageSize = 50,
}: {
  filters?: T;
  pagination: PaginationState;
  sorting: SortingState;
  setPagination: Dispatch<React.SetStateAction<PaginationState>>;
  searchParamsLoaded?: boolean;
  defaultPageSize?: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredFilter, setFilteredFilter] = useState<T>();
  const [debouncedFiltersValue] = useDebounce(filteredFilter, 300);

  const skipFirstFilterValue = useRef<boolean>(true);
  const resetFilters = useRef<boolean>(false);

  useEffect(() => {
    if (!filters) {
      return;
    }
    if (skipFirstFilterValue.current) {
      skipFirstFilterValue.current = false;
      return;
    }
    if (Object.keys(filters).length === 0) {
      // Filters is an empty object, so reset filters. Set pagination to first page.
      resetFilters.current = true;
      setPagination((old) => ({ ...old, pageIndex: 0 }));
      return;
    }
    setFilteredFilter(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!debouncedFiltersValue) {
      return;
    }
    Object.keys(debouncedFiltersValue).forEach((key) => {
      const value = (debouncedFiltersValue as Record<string, unknown>)[key];
      if (value) {
        searchParams.set(key, value as string);
      } else {
        searchParams.delete(key);
      }
    });

    // Every time filters change, reset to the first page.
    searchParams.delete(SearchParam.PAGE);

    setPagination((old) => ({ ...old, pageIndex: 0 }));
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFiltersValue]);

  useEffect(() => {
    if (!searchParamsLoaded) {
      return;
    }
    if (resetFilters.current) {
      const newParams = new URLSearchParams({
        [SearchParam.PAGE_SIZE]: pagination.pageSize.toString(),
      });
      setSearchParams(newParams, { replace: true });
      resetFilters.current = false;
      return;
    }
    searchParams.delete(SearchParam.PAGE_SIZE);
    searchParams.delete(SearchParam.PAGE);
    if (pagination.pageSize !== defaultPageSize) {
      searchParams.set(SearchParam.PAGE_SIZE, pagination.pageSize.toString());
    }

    if (pagination.pageIndex > 0) {
      searchParams.set(SearchParam.PAGE, pagination.pageIndex.toString());
    }

    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  useEffect(() => {
    if (!searchParamsLoaded) {
      return;
    }
    const sortParam = tableSortingStateToSearchParam(sorting);
    if (sortParam) {
      searchParams.set(SearchParam.ORDER_BY, sortParam);
    } else {
      searchParams.delete(SearchParam.ORDER_BY);
    }
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);
}
